import { useEffect } from 'react';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

// The map region *data* (coordinates/labels) comes from the DB via
// /api/dex/map-regions and is already fast/cached — the slow part users
// actually feel is these route image files, served as plain static assets.
// nginx already sends them `Cache-Control: public, immutable` for a year
// (see frontend/nginx.conf), so once a browser has fetched one it never
// re-fetches it — the only problem is the FIRST fetch happening exactly
// when the user opens /map instead of before. Prefetching them in the
// background as soon as the app loads (not gated behind visiting the map
// page first) means that by the time someone actually clicks "Map", every
// image is already sitting in the browser's HTTP cache.
//
// This is the fixed list of files in public/images/maps/ — update it if
// map images are ever added or removed (see getMapImagePath in
// InteractiveMap.tsx for how a region name turns into one of these paths).
const MAP_IMAGE_URLS = Array.from({ length: 20 }, (_, i) => `/images/maps/route_${i + 1}.png`);

/** Renders nothing — just warms the browser cache for the map's route
 *  images in the background. Mount once, near the app root. */
const MapImagePreloader: React.FC = () => {
  const { flags } = useFeatureFlags();

  useEffect(() => {
    if (!flags.feature_page_map) return; // map disabled for this deployment — don't spend the bandwidth
    // Wait a couple seconds so this never competes with whatever the user
    // actually opened the app to load first (game data, the current page's
    // own chunk/images) — it only needs to finish before they click "Map",
    // not before anything else.
    const timer = setTimeout(() => {
      // Plain Image() warmup rather than <link rel="prefetch"> — works
      // identically in every browser (Safari's prefetch-link support is
      // unreliable), and a decoded/cached Image object needs nothing more:
      // the browser's HTTP cache is what actually makes the later <img> in
      // InteractiveMap instant, not this object itself.
      for (const src of MAP_IMAGE_URLS) {
        const img = new Image();
        img.src = src;
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [flags.feature_page_map]);

  return null;
};

export default MapImagePreloader;
